import { ChevronDown, Search } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { COUNTRIES, flagEmoji, getCountry, parsePhone, toE164 } from '../../utils/phone';
import { FormInput } from './FormInput';
import { VStack } from './Stack';
import { Text } from './Text';

interface CountryPickerListProps {
  onSelect: (iso: string) => void;
  searchPlaceholder?: string;
  title?: string;
  testIDPrefix?: string;
}

/**
 * Pure, searchable country list. Kept free of `Modal` so it can be rendered and
 * exercised directly in tests (react-test-renderer cannot mount an open Modal).
 */
export function CountryPickerList({
  onSelect,
  searchPlaceholder,
  title,
  testIDPrefix,
}: Readonly<CountryPickerListProps>) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    const digits = q.replace(/\D/g, '');
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (digits.length > 0 && c.dial.startsWith(digits)) ||
        c.iso.toLowerCase() === q,
    );
  }, [query]);

  return (
    <View style={styles.modalCard} accessibilityRole="menu">
      {title ? (
        <Text variant="h6" color={colors.text.primary} style={styles.modalTitle}>
          {title}
        </Text>
      ) : null}

      <View style={styles.searchRow}>
        <Search size={16} color={colors.text.tertiary} />
        <FormInput
          testID={testIDPrefix ? `${testIDPrefix}_search` : undefined}
          value={query}
          onChangeText={setQuery}
          placeholder={searchPlaceholder}
          autoCorrect={false}
          autoCapitalize="none"
          style={styles.searchInput}
        />
      </View>

      <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
        {filtered.map((c) => (
          <Pressable
            key={c.iso}
            testID={testIDPrefix ? `${testIDPrefix}_option_${c.iso}` : undefined}
            onPress={() => onSelect(c.iso)}
            accessibilityRole="button"
            style={({ pressed }) => [styles.option, pressed && styles.optionPressed]}
          >
            <Text style={styles.optionFlag}>{flagEmoji(c.iso)}</Text>
            <Text variant="bodySmall" color={colors.text.primary} style={styles.optionName}>
              {c.name}
            </Text>
            <Text variant="bodySmall" color={colors.text.tertiary}>
              +{c.dial}
            </Text>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );
}

interface PhoneNumberInputProps {
  /** Stored E.164 value (`+57...`) or an empty string. */
  value: string;
  /** Emits the combined E.164 value (empty string when the national part is blank). */
  onChangeText: (e164: string) => void;
  label: string;
  required?: boolean;
  error?: string;
  testID?: string;
  placeholder?: string;
  /** Search field placeholder inside the country modal. */
  searchPlaceholder?: string;
  /** Title shown at the top of the country modal. */
  modalTitle?: string;
}

export function PhoneNumberInput({
  value,
  onChangeText,
  label,
  required,
  error,
  testID,
  placeholder,
  searchPlaceholder,
  modalTitle,
}: Readonly<PhoneNumberInputProps>) {
  const initial = useMemo(() => parsePhone(value), [value]);
  const [iso, setIso] = useState(initial.iso);
  const [national, setNational] = useState(initial.national);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Re-hydrate when the value is replaced from the outside (e.g. edit screen
  // loading a lead, or a successful submit clearing the form).
  const lastEmitted = useRef(value);
  useEffect(() => {
    if (value !== lastEmitted.current) {
      const parsed = parsePhone(value);
      setIso(parsed.iso);
      setNational(parsed.national);
      lastEmitted.current = value;
    }
  }, [value]);

  const country = getCountry(iso);

  const emit = (nextIso: string, nextNational: string) => {
    const e164 = toE164(getCountry(nextIso).dial, nextNational);
    lastEmitted.current = e164;
    onChangeText(e164);
  };

  const handleNationalChange = (text: string) => {
    // Pasting a full international number (with a "+") re-detects the country.
    if (text.includes('+')) {
      const parsed = parsePhone(text);
      setIso(parsed.iso);
      setNational(parsed.national);
      emit(parsed.iso, parsed.national);
      return;
    }
    const digits = text.replace(/\D/g, '');
    setNational(digits);
    emit(iso, digits);
  };

  const handlePickCountry = (nextIso: string) => {
    setIso(nextIso);
    setModalOpen(false);
    emit(nextIso, national);
  };

  const shellStyle = [
    styles.shell,
    isHovered && styles.shellHovered,
    isFocused && styles.shellFocused,
    Boolean(error) && styles.shellError,
  ];

  return (
    <VStack spacing="xs">
      <Text variant="caption" color={colors.text.secondary} style={styles.label}>
        {label}
        {required && (
          <Text variant="caption" color={colors.status.error.main}>
            {' '}
            *
          </Text>
        )}
      </Text>

      <View
        style={shellStyle}
        onPointerEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
        onPointerLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      >
        <Pressable
          testID={testID ? `${testID}_country` : undefined}
          onPress={() => setModalOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={`${country.name} (+${country.dial})`}
          style={({ pressed }) => [styles.countryButton, pressed && styles.countryButtonPressed]}
        >
          <Text style={styles.flag}>{flagEmoji(country.iso)}</Text>
          <Text variant="bodySmall" color={colors.text.primary} style={styles.dial}>
            +{country.dial}
          </Text>
          <ChevronDown size={14} color={colors.text.tertiary} />
        </Pressable>

        <View style={styles.divider} />

        <FormInput
          testID={testID}
          value={national}
          onChangeText={handleNationalChange}
          placeholder={placeholder}
          keyboardType="phone-pad"
          autoCorrect={false}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={styles.input}
        />
      </View>

      {error ? (
        <Text
          variant="caption"
          color={colors.status.error.main}
          testID={testID ? `${testID}_error` : undefined}
        >
          {error}
        </Text>
      ) : null}

      <Modal
        transparent
        visible={modalOpen}
        animationType="fade"
        onRequestClose={() => setModalOpen(false)}
      >
        <Pressable
          style={styles.backdrop}
          onPress={() => setModalOpen(false)}
          accessibilityLabel="Close country selector"
        />
        <CountryPickerList
          onSelect={handlePickCountry}
          searchPlaceholder={searchPlaceholder}
          title={modalTitle}
          testIDPrefix={testID ? `${testID}_country` : undefined}
        />
      </Modal>
    </VStack>
  );
}

const styles = StyleSheet.create({
  label: {
    fontWeight: '500',
    letterSpacing: 0.3,
  },
  shell: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    minHeight: 48,
  },
  shellHovered: {
    borderColor: colors.border.default,
    backgroundColor: colors.background.elevated,
  },
  shellFocused: {
    borderColor: colors.primary.main,
    backgroundColor: colors.background.elevated,
  },
  shellError: {
    borderColor: colors.status.error.main,
  },
  countryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    ...Platform.select({ web: { cursor: 'pointer' } as object }),
  },
  countryButtonPressed: {
    opacity: 0.6,
  },
  flag: {
    fontSize: 18,
  },
  dial: {
    fontWeight: '600',
  },
  divider: {
    width: 1,
    alignSelf: 'stretch',
    marginVertical: tokens.spacing.sm,
    backgroundColor: colors.border.subtle,
  },
  input: {
    flex: 1,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    paddingHorizontal: tokens.spacing.md,
    minHeight: 44,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalCard: {
    position: 'absolute',
    top: '12%',
    alignSelf: 'center',
    width: '90%',
    maxWidth: 460,
    maxHeight: '76%',
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: tokens.spacing.lg,
    ...tokens.shadow?.md,
  },
  modalTitle: {
    fontWeight: '700',
    marginBottom: tokens.spacing.md,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    paddingHorizontal: tokens.spacing.md,
    minHeight: 44,
    marginBottom: tokens.spacing.sm,
  },
  searchInput: {
    flex: 1,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight: 44,
  },
  list: {
    flexGrow: 0,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    ...Platform.select({ web: { cursor: 'pointer' } as object }),
  },
  optionPressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  optionFlag: {
    fontSize: 20,
  },
  optionName: {
    flex: 1,
    fontWeight: '500',
  },
});
